import React, { useEffect, useRef, useState } from "react";
import { getStoredTheme } from "../lib/theme";

export default function TradingViewChart({ symbol = "OANDA:XAUUSD", height = 480 }) {
  const containerRef = useRef(null);
  const [theme, setTheme] = useState(getStoredTheme());
  const [dims, setDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handler = (e) => setTheme(e.detail.theme);
    window.addEventListener("ft:theme-change", handler);
    return () => window.removeEventListener("ft:theme-change", handler);
  }, []);

  // Track real container dimensions so the widget fills its parent exactly
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setDims({ width: Math.floor(rect.width), height: Math.floor(rect.height) || height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [height]);

  const w = dims.width;
  const h = dims.height || height;

  useEffect(() => {
    const node = containerRef.current;
    if (!node || !w) return;
    node.innerHTML = "";

    const widgetDiv = document.createElement("div");
    widgetDiv.className = "tradingview-widget-container__widget";
    widgetDiv.style.height = `${h}px`;
    widgetDiv.style.width = `${w}px`;
    node.appendChild(widgetDiv);

    const isDark = theme !== "light";
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.async = true;
    script.type = "text/javascript";
    script.innerHTML = JSON.stringify({
      width: w,
      height: h,
      symbol,
      interval: "D",
      timezone: "Europe/Berlin",
      theme: isDark ? "dark" : "light",
      style: "1",
      locale: "de_DE",
      toolbar_bg: isDark ? "#0a0a0a" : "#ffffff",
      enable_publishing: false,
      hide_top_toolbar: false,
      hide_legend: false,
      withdateranges: true,
      allow_symbol_change: true,
      save_image: false,
      backgroundColor: isDark ? "rgba(9, 9, 11, 1)" : "rgba(255, 255, 255, 1)",
      gridColor: isDark ? "rgba(39, 39, 42, 0.5)" : "rgba(228, 228, 231, 0.6)",
      support_host: "https://www.tradingview.com",
    });
    node.appendChild(script);
  }, [symbol, h, theme, w]);

  return (
    <div
      ref={containerRef}
      className="tradingview-widget-container border border-zinc-800 bg-zinc-950 w-full h-full overflow-hidden"
      style={{ minHeight: `clamp(280px, 70vw, ${height}px)` }}
      data-testid="tradingview-chart"
    />
  );
}
